/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
    Box,
    Button,
    Card,
    CardContent,
    Checkbox,
    FormControl,
    FormControlLabel,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { Remove } from '../../../assets/icons/remove';
import { styles } from './styles';
import { Plus } from '../../../assets/icons/plus';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import {
    usePreCreateChangeRequestPolicyMutation,
    useCreateChangeRequestPolicyMutation,
} from '../../../api/changeRequestPolicies';
import { useFormik } from 'formik';
import * as Yup from 'yup';

export default function CRPoliciesAdd(props) {
    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();
    const { user } = useSelector((state) => state.session);
    const [crPolicyPreCreate, setCrPolicyPreCreate] = useState({});
    const {
        availableStatuses = [],
        majorApprovers = [],
        minorApprovers = [],
        clientApprovers = [],
        onedemandApprovers = [],
    } = crPolicyPreCreate;

    const [employeeList, setEmployeeList] = useState([
        {
            id: `employeeList_0`,
            label: 'Approver',
            selectValue: '',
            type: 'Minor',
            isActive: true,
        },
        {
            id: `employeeList_1`,
            label: 'Approver',
            selectValue: '',
            type: 'Major',
            isActive: true,
        },
        {
            id: `employeeList_2`,
            label: 'Approver',
            selectValue: '',
            type: 'Client',
            isActive: true,
        },
        {
            id: `employeeList_3`,
            label: 'Approver',
            selectValue: '',
            type: 'OneDemand',
            isActive: true,
        },
    ]);

    const formik = useFormik({
        initialValues: {
            name: '',
            status: 'Active',
            isOnedemandDefault: false,
        },
        enableReinitialize: true,
        validationSchema: Yup.object().shape({
            name: Yup.string()
                .max(255)
                .required('Name is required')
                .matches(
                    /^[A-Za-z0-9 :,\-]+$/,
                    'Name can contain only letters, numbers, spaces, commas, colon, and hyphens',
                ),
            status: Yup.string().max(255).required('Status is required'),
        }),
        onSubmit: async (values, helpers) => {
            try {
                handleCreate(values);
                // helpers.resetForm();
                helpers.setStatus({ success: true });
                helpers.setSubmitting(false);
            } catch (err) {
                console.error(err);
                helpers.setStatus({ success: false });
                helpers.setErrors({
                    submit: err.message,
                });
                helpers.setSubmitting(false);
            }
        },
    });

    const [preCreateChangeRequestPolicy, { isLoading: isPreCreateLoading }] = usePreCreateChangeRequestPolicyMutation();
    const [createChangeRequestPolicy, { isLoading: isCreateLoading }] = useCreateChangeRequestPolicyMutation();

    useEffect(() => {
        const fetchPreCreateData = async () => {
            try {
                const response = await preCreateChangeRequestPolicy().unwrap();
                if (!response) return;
                if (response?.status === 'ERROR') throw new Error(response?.message);
                setCrPolicyPreCreate(response);
            } catch (err) {
                if (err?.statusCode === 400) enqueueSnackbar(err?.data?.message, { variant: 'error' });
                else enqueueSnackbar(err?.message || 'Something went wrong!', { variant: 'error' });
            }
        };

        fetchPreCreateData();
    }, [preCreateChangeRequestPolicy, enqueueSnackbar]);

    useEffect(() => {
        if (!!Object.keys(createChangeRequestPolicy).length) {
            enqueueSnackbar(createChangeRequestPolicy.message, {
                variant: 'success',
            });
            navigate('/changerequest/crpolicies');
        }
    }, [createChangeRequestPolicy]);

    const handleRemove = (id, type) => {
        if (type === 'Client') return;
        setEmployeeList((prev) =>
            prev.map((item) => (item.id === id && item.type === type ? { ...item, isActive: false } : item)),
        );
    };

    const handleCreate = async (data) => {
        let majorApproverIds = [];
        let minorApproverIds = [];
        let clientApproverIdsList = [];
        let onedemandApproverIds = [];

        const { name, status, isOnedemandDefault } = data;

        const payload = {
            name,
            status,
            isOnedemandDefault: !!isOnedemandDefault,
        };

        employeeList
            .filter((item) => item.isActive)
            .forEach((item) => {
                if (item.selectValue === '') return;
                if (isOnedemandDefault) {
                    if (item.type === 'OneDemand') onedemandApproverIds.push(item.selectValue.id);
                    return;
                }
                if (item.type === 'Minor') minorApproverIds.push(item.selectValue.id);
                else if (item.type === 'Major') majorApproverIds.push(item.selectValue.id);
                else if (item.type === 'Client') clientApproverIdsList.push(item.selectValue.id);
            });

        if (isOnedemandDefault) {
            payload['approverCount'] = onedemandApproverIds.length;
            payload['onedemandApproverIds'] = `${onedemandApproverIds}`;
            // Do not send majorApproverIds / minorApproverIds / clientApproverId for default policy
        } else {
            payload['approverCount'] = minorApproverIds.length + majorApproverIds.length + clientApproverIdsList.length;
            payload['majorApproverIds'] = `${majorApproverIds}`;
            payload['minorApproverIds'] = `${minorApproverIds}`;
            payload['clientApproverId'] = clientApproverIdsList.length > 0 ? String(clientApproverIdsList[0]) : '';
            // Do not send onedemandApproverIds for standard policy
        }

        try {
            const bodyFormData = new FormData();
            Object.keys(payload).forEach((key) => {
                bodyFormData.append(key, payload[key]);
            });

            const response = await createChangeRequestPolicy(bodyFormData).unwrap();
            if (!response) return;
            if (response?.status === 'ERROR') throw new Error(response?.message);
            if (response?.status === 'SUCCESS') {
                enqueueSnackbar(response?.message, {
                    variant: 'success',
                });
                navigate('/changerequest/crpolicies');
            } else {
                enqueueSnackbar(response?.message, {
                    variant: 'error',
                });
            }
        } catch (err) {
            if (err?.statusCode === 400) enqueueSnackbar(err?.data?.message, { variant: 'error' });
            else enqueueSnackbar(err?.message || 'Something went wrong!', { variant: 'error' });
        }
    };

    const handleAddEmployeeList = (type) => {
        if (type === 'Client') return;
        setEmployeeList((prev) => {
            let maxIdx = -1;
            prev.forEach((item) => {
                const m = /^employeeList_(\d+)$/.exec(item.id);
                if (m) maxIdx = Math.max(maxIdx, parseInt(m[1], 10));
            });
            return [
                ...prev,
                {
                    id: `employeeList_${maxIdx + 1}`,
                    label: 'Approver',
                    selectValue: '',
                    type,
                    isActive: true,
                },
            ];
        });
    };

    const handleChange = (e, id, type) => {
        setEmployeeList((prev) =>
            prev.map((item) =>
                item.id === id && item.type === type ? { ...item, selectValue: e.target.value } : item,
            ),
        );
    };

    const isOnedemandDefault = !!formik.values.isOnedemandDefault;

    const isDisabled = useMemo(() => {
        if (isOnedemandDefault) {
            const activeOneDemandList = employeeList?.filter(
                (item) => item.isActive && item.type === 'OneDemand' && item.selectValue !== '',
            );
            return activeOneDemandList.length === 0;
        }
        const activeMajorList = employeeList?.filter(
            (item) => item.isActive && item.type === 'Major' && item.selectValue !== '',
        );
        const activeMinorList = employeeList?.filter(
            (item) => item.isActive && item.type === 'Minor' && item.selectValue !== '',
        );
        const activeClientList = employeeList?.filter(
            (item) => item.isActive && item.type === 'Client' && item.selectValue !== '',
        );
        return (
            activeMajorList.length === 0 ||
            activeMinorList.length === 0 ||
            (user.isClientHqEnabled && activeClientList.length === 0)
        );
    }, [employeeList, isOnedemandDefault, user.isClientHqEnabled]);

    const { majorApproverList, minorApproverList, clientApproverList, onedemandApproverList } = useMemo(() => {
        const minorEmployeeSelectedList = employeeList
            ?.filter((item) => item.type === 'Minor' && item.isActive && item.selectValue !== '')
            ?.map((item) => parseInt(item.selectValue.id));
        const majorEmployeeSelectedList = employeeList
            ?.filter((item) => item.type === 'Major' && item.isActive && item.selectValue !== '')
            ?.map((item) => parseInt(item.selectValue.id));
        const clientEmployeeSelectedList = employeeList
            ?.filter((item) => item.type === 'Client' && item.isActive && item.selectValue !== '')
            ?.map((item) => parseInt(item.selectValue.id));
        const onedemandEmployeeSelectedList = employeeList
            ?.filter((item) => item.type === 'OneDemand' && item.isActive && item.selectValue !== '')
            ?.map((item) => parseInt(item.selectValue.id));
        const minorApproverList = minorApprovers?.filter((item) => minorEmployeeSelectedList.indexOf(item.id) < 0);
        const majorApproverList = majorApprovers?.filter((item) => majorEmployeeSelectedList.indexOf(item.id) < 0);
        const clientApproverList = clientApprovers?.filter((item) => clientEmployeeSelectedList.indexOf(item.id) < 0);
        const onedemandApproverList = onedemandApprovers?.filter(
            (item) => onedemandEmployeeSelectedList.indexOf(item.id) < 0,
        );
        return { majorApproverList, minorApproverList, clientApproverList, onedemandApproverList };
    }, [majorApprovers, minorApprovers, clientApprovers, onedemandApprovers, employeeList]);

    const { minorEmployeeList, majorEmployeeList, clientEmployeeList, onedemandEmployeeList } = useMemo(() => {
        const minorEmployeeList = employeeList?.filter((item) => item.type === 'Minor' && item.isActive);
        const majorEmployeeList = employeeList?.filter((item) => item.type === 'Major' && item.isActive);
        const clientEmployeeList = employeeList?.filter((item) => item.type === 'Client' && item.isActive);
        const onedemandEmployeeList = employeeList?.filter((item) => item.type === 'OneDemand' && item.isActive);
        return { minorEmployeeList, majorEmployeeList, clientEmployeeList, onedemandEmployeeList };
    }, [employeeList]);

    return (
        <Box>
            <Box sx={styles.contentHeaderWrapper}>
                <Typography variant="h4">Add Change Policy</Typography>
            </Box>
            <Box sx={styles.contentBodyWrapper}>
                <Card {...props}>
                    <CardContent>
                        <Grid container direction="column" spacing={2}>
                            <Grid item>
                                <Button
                                    startIcon={<ChevronLeftIcon />}
                                    variant="text"
                                    color="secondary"
                                    onClick={() => navigate('/changerequest/crpolicies')}
                                >
                                    Back
                                </Button>
                            </Grid>
                        </Grid>
                        <Grid item>
                            <Box sx={styles.basicInformation} component="form" noValidate autoComplete="off">
                                <Typography variant="h6" sx={styles.basicInformationText}>
                                    Basic information
                                </Typography>
                                <Box sx={styles.inputBox}>
                                    <Grid container columnSpacing={6} rowSpacing={3}>
                                        <Grid item xs={6}>
                                            <TextField
                                                error={Boolean(formik.touched.name && formik.errors.name)}
                                                fullWidth
                                                helperText={formik.touched.name && formik.errors.name}
                                                required
                                                id="name"
                                                label="Name"
                                                name="name"
                                                onBlur={formik.handleBlur}
                                                onChange={formik.handleChange}
                                                value={formik.values.name}
                                                size="small"
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextField
                                                fullWidth
                                                error={Boolean(formik.touched.status && formik.errors.status)}
                                                label="Available Status"
                                                name={'status'}
                                                size="small"
                                                id="status"
                                                helperText={formik.touched.status && formik.errors.status}
                                                onBlur={formik.handleBlur}
                                                onChange={formik.handleChange}
                                                select
                                                children={availableStatuses.map((status) => (
                                                    <MenuItem key={status} value={status}>
                                                        {status}
                                                    </MenuItem>
                                                ))}
                                                value={formik.values.status}
                                            />
                                        </Grid>
                                        {user.isClientHqEnabled && (
                                            <Grid item xs={12}>
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            name="isOnedemandDefault"
                                                            checked={formik.values.isOnedemandDefault}
                                                            onChange={formik.handleChange}
                                                        />
                                                    }
                                                    label="1Demand Default Change Policy"
                                                />
                                            </Grid>
                                        )}
                                    </Grid>
                                </Box>
                            </Box>
                            {isOnedemandDefault ? (
                                <React.Fragment>
                                    <Box sx={styles.bottomBLine}></Box>
                                    <Box sx={styles.additionalPreOfferButton}>
                                        <Typography variant="h6" sx={styles.basicInformationText}>
                                            1Demand change request approvers
                                        </Typography>
                                        <Button
                                            color="primary"
                                            onClick={() => handleAddEmployeeList('OneDemand')}
                                            size="large"
                                            variant="text"
                                            startIcon={<Plus />}
                                        >
                                            Add Approver
                                        </Button>
                                    </Box>
                                    <Box sx={styles.employeeListsBox}>
                                        {onedemandEmployeeList?.map((item) => (
                                            <Box key={item.id} sx={styles.inputsAdditionalPreOfferBox}>
                                                <FormControl sx={styles.inputsAdditionalPreOffer} size="small">
                                                    <InputLabel>{item.label}</InputLabel>
                                                    <Select
                                                        label={item.label}
                                                        defaultValue=""
                                                        value={item.selectValue}
                                                        onChange={(e) => handleChange(e, item.id, 'OneDemand')}
                                                    >
                                                        {(item.selectValue === ''
                                                            ? onedemandApproverList
                                                            : [item.selectValue, ...onedemandApproverList]
                                                        )?.map((val) => (
                                                            <MenuItem value={val} key={val.id}>
                                                                {val.fullNameWithRole}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                                {onedemandEmployeeList.length > 1 && (
                                                    <Box sx={styles.removeButtonBox}>
                                                        <Button
                                                            onClick={() => handleRemove(item.id, 'OneDemand')}
                                                            size="large"
                                                            sx={styles.removeButton}
                                                            variant="text"
                                                            startIcon={<Remove />}
                                                        >
                                                            Remove
                                                        </Button>
                                                    </Box>
                                                )}
                                            </Box>
                                        ))}
                                    </Box>
                                </React.Fragment>
                            ) : (
                                <React.Fragment>
                                    <Box sx={styles.bottomBLine}></Box>
                                    <Box sx={styles.additionalPreOfferButton}>
                                        <Typography variant="h6" sx={styles.basicInformationText}>
                                            Minor change request approvers
                                        </Typography>

                                        <Button
                                            color="primary"
                                            onClick={() => handleAddEmployeeList('Minor')}
                                            size="large"
                                            variant="text"
                                            startIcon={<Plus />}
                                        >
                                            Add Approver
                                        </Button>
                                    </Box>
                                    <Box sx={styles.employeeListsBox}>
                                        {minorEmployeeList?.map((item) => (
                                            <Box key={item.id} sx={styles.inputsAdditionalPreOfferBox}>
                                                <FormControl sx={styles.inputsAdditionalPreOffer} size="small">
                                                    <InputLabel id="demo-simple-select-label">{item.label}</InputLabel>
                                                    <Select
                                                        labelId="demo-simple-select-label"
                                                        id="demo-simple-select"
                                                        label={item.label}
                                                        defaultValue=""
                                                        value={item.selectValue}
                                                        onChange={(e) => handleChange(e, item.id, 'Minor')}
                                                    >
                                                        {(item.selectValue === ''
                                                            ? minorApproverList
                                                            : [item.selectValue, ...minorApproverList]
                                                        )?.map((val) => (
                                                            <MenuItem value={val} key={val.id}>
                                                                {val.fullNameWithRole}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                                {minorEmployeeList.length > 1 && (
                                                    <Box sx={styles.removeButtonBox}>
                                                        <Button
                                                            onClick={() => handleRemove(item.id, 'Minor')}
                                                            size="large"
                                                            sx={styles.removeButton}
                                                            variant="text"
                                                            startIcon={<Remove />}
                                                        >
                                                            Remove
                                                        </Button>
                                                    </Box>
                                                )}
                                            </Box>
                                        ))}
                                    </Box>
                                    <Box sx={styles.bottomBLine}></Box>
                                    <Box sx={styles.additionalPreOfferButton}>
                                        <Typography variant="h6" sx={styles.basicInformationText}>
                                            Major change request approvers
                                        </Typography>

                                        <Button
                                            color="primary"
                                            onClick={() => handleAddEmployeeList('Major')}
                                            size="large"
                                            variant="text"
                                            startIcon={<Plus />}
                                        >
                                            Add Approver
                                        </Button>
                                    </Box>
                                    <Box sx={styles.employeeListsBox}>
                                        {majorEmployeeList?.map((item) => (
                                            <Box key={item.id} sx={styles.inputsAdditionalPreOfferBox}>
                                                <FormControl sx={styles.inputsAdditionalPreOffer} size="small">
                                                    <InputLabel id="demo-simple-select-label">{item.label}</InputLabel>
                                                    <Select
                                                        labelId="demo-simple-select-label"
                                                        id="demo-simple-select"
                                                        label={item.label}
                                                        defaultValue=""
                                                        value={item.selectValue}
                                                        onChange={(e) => handleChange(e, item.id, 'Major')}
                                                    >
                                                        {(item.selectValue === ''
                                                            ? majorApproverList
                                                            : [item.selectValue, ...majorApproverList]
                                                        )?.map((val) => (
                                                            <MenuItem value={val} key={val.id}>
                                                                {val.fullNameWithRole}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                                {majorEmployeeList.length > 1 && (
                                                    <Box sx={styles.removeButtonBox}>
                                                        <Button
                                                            onClick={() => handleRemove(item.id, 'Major')}
                                                            size="large"
                                                            sx={styles.removeButton}
                                                            variant="text"
                                                            startIcon={<Remove />}
                                                        >
                                                            Remove
                                                        </Button>
                                                    </Box>
                                                )}
                                            </Box>
                                        ))}
                                    </Box>
                                    {user.isClientHqEnabled && (
                                        <React.Fragment>
                                            <Box sx={styles.bottomBLine}></Box>
                                            <Box sx={styles.additionalPreOfferButton}>
                                                <Typography variant="h6" sx={styles.basicInformationText}>
                                                    Client change request approver
                                                </Typography>
                                            </Box>
                                            <Box sx={styles.employeeListsBox}>
                                                {clientEmployeeList?.map((item) => (
                                                    <Box key={item.id} sx={styles.inputsAdditionalPreOfferBox}>
                                                        <FormControl sx={styles.inputsAdditionalPreOffer} size="small">
                                                            <InputLabel id="demo-simple-select-label-client">
                                                                {item.label}
                                                            </InputLabel>
                                                            <Select
                                                                labelId="demo-simple-select-label-client"
                                                                id="demo-simple-select-client"
                                                                label={item.label}
                                                                defaultValue=""
                                                                value={item.selectValue}
                                                                onChange={(e) => handleChange(e, item.id, 'Client')}
                                                            >
                                                                {(item.selectValue === ''
                                                                    ? clientApproverList
                                                                    : [item.selectValue, ...clientApproverList]
                                                                )?.map((val) => (
                                                                    <MenuItem value={val} key={val.id}>
                                                                        {val.fullNameWithRole}
                                                                    </MenuItem>
                                                                ))}
                                                            </Select>
                                                        </FormControl>
                                                    </Box>
                                                ))}
                                            </Box>
                                        </React.Fragment>
                                    )}
                                </React.Fragment>
                            )}
                            <Box>
                                <Box sx={styles.bottomBLine}></Box>
                                <Box sx={styles.createButtonBox}>
                                    <Button
                                        disabled={!formik.dirty || (formik.values.status === 'Active' && isDisabled)}
                                        color="primary"
                                        onClick={formik.handleSubmit}
                                        size="medium"
                                        sx={styles.createButton}
                                        variant="contained"
                                    >
                                        Create
                                    </Button>
                                </Box>
                            </Box>
                        </Grid>
                    </CardContent>
                </Card>
            </Box>
        </Box>
    );
}
